import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { FileUp, Camera, PenTool, Mic, FileText, Loader2, ArrowLeft } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import ReviewModal from "./ReviewModal";

export default function AddSomethingModal({ open, onOpenChange, onSaved }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState(null); // null | text | voice
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);       // AI extraction in progress
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [extracted, setExtracted] = useState(null);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
  };

  const reset = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") { try { mr.stop(); } catch (e) { /* noop */ } }
    stopStream();
    setMode(null); setText(""); setLoading(false); setRecording(false); setTranscribing(false);
  };

  const close = () => { onOpenChange(false); setTimeout(reset, 200); };

  const options = [
    { id: "type_text", label: t("add.optTypeTitle"), desc: t("add.optTypeDesc"), icon: PenTool, action: () => setMode("text") },
    { id: "voice", label: t("add.optVoiceTitle"), desc: t("add.optVoiceDesc"), icon: Mic, action: () => setMode("voice") },
    { id: "camera", label: t("add.optCameraTitle"), desc: t("add.optCameraDesc"), icon: Camera, accept: "image/*", capture: "environment" },
    { id: "upload_img", label: t("add.optUploadImgTitle"), desc: t("add.optUploadImgDesc"), icon: FileUp, accept: "image/*" },
    { id: "upload_doc", label: t("add.optUploadDocTitle"), desc: t("add.optUploadDocDesc"), icon: FileText, accept: ".pdf" },
  ];

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("lang", i18n.language);
      const { data } = await api.post("/extract/file", form, { headers: { "Content-Type": "multipart/form-data" } });
      setExtracted(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("add.extractionFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleText = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/extract/text", { text, lang: i18n.language });
      setExtracted(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("add.extractionFailed"));
    } finally {
      setLoading(false);
    }
  };

  // ---- Voice ----
  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error(t("voice.notSupported")); setMode(null); return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => transcribe(mr.mimeType);
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (err) {
      stopStream();
      toast.error(t("voice.micDenied"));
      setMode(null);
    }
  };

  const stopRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
    setRecording(false);
  };

  const transcribe = async (mimeType) => {
    stopStream();
    setTranscribing(true);
    try {
      const type = mimeType || "audio/webm";
      const ext = type.includes("mp4") ? "mp4" : "webm";
      const blob = new Blob(chunksRef.current, { type });
      const form = new FormData();
      form.append("file", blob, `voice.${ext}`);
      form.append("lang", i18n.language);
      const { data } = await api.post("/voice/transcribe", form, { headers: { "Content-Type": "multipart/form-data" } });
      setText(data.text || "");
      setMode("text"); // editable transcript review before extraction
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("voice.failed"));
    } finally {
      setTranscribing(false);
    }
  };

  const busy = loading || transcribing;

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
        <DialogContent className="sm:max-w-md" data-testid="add-something-modal">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              {mode && !busy && (
                <button onClick={reset} className="text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /></button>
              )}
              {t("add.title")}
            </DialogTitle>
            <DialogDescription>{t("add.description")}</DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="py-10 flex flex-col items-center gap-3 text-muted-foreground">
              <Loader2 className="h-7 w-7 animate-spin brand-text" />
              <p className="text-sm">{t("add.reading")}</p>
            </div>
          )}

          {transcribing && (
            <div className="py-10 flex flex-col items-center gap-3 text-muted-foreground" data-testid="voice-transcribing">
              <Loader2 className="h-7 w-7 animate-spin brand-text" />
              <p className="text-sm">{t("voice.transcribing")}</p>
            </div>
          )}

          {!busy && !mode && (
            <div className="space-y-3 py-2">
              {options.map((o) => (
                <label key={o.id} data-testid={`add-option-${o.id}`}
                  className="flex items-center gap-4 p-4 rounded-xl border border-border hover:border-brand hover:bg-accent/40 cursor-pointer transition-colors"
                  onClick={() => o.action && o.action()}>
                  <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                    <o.icon className="h-5 w-5 brand-text" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{o.label}</p>
                    <p className="text-xs text-muted-foreground">{o.desc}</p>
                  </div>
                  {o.accept && (
                    <input type="file" accept={o.accept} {...(o.capture ? { capture: o.capture } : {})} className="hidden"
                      data-testid={`file-input-${o.id}`}
                      onClick={(ev) => { ev.currentTarget.value = ""; }}
                      onChange={handleFile} />
                  )}
                </label>
              ))}
            </div>
          )}

          {!busy && mode === "voice" && (
            <div className="py-8 flex flex-col items-center gap-5" data-testid="voice-recorder">
              <button data-testid="voice-record-btn" onClick={recording ? stopRecording : startRecording}
                className={`h-24 w-24 rounded-full flex items-center justify-center transition-all ${
                  recording ? "bg-red-500 text-white scale-105 animate-pulse" : "brand-bg text-white hover:opacity-90"
                }`}>
                <Mic className="h-10 w-10" />
              </button>
              <p className="text-sm text-muted-foreground">{recording ? t("voice.listening") : t("voice.tapToStart")}</p>
            </div>
          )}

          {!busy && mode === "text" && (
            <div className="space-y-3 py-2">
              <Textarea data-testid="text-input-field" value={text} onChange={(e) => setText(e.target.value)}
                rows={4} placeholder={t("add.textPlaceholder")} />
              <Button data-testid="btn-extract-text" onClick={handleText} disabled={!text.trim()}
                className="w-full brand-bg text-white hover:opacity-90">
                {t("add.readCta")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ReviewModal extracted={extracted} onClose={() => setExtracted(null)}
        onSaved={() => { setExtracted(null); close(); onSaved && onSaved(); }} />
    </>
  );
}
